import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth-context";
import { useLocation } from "wouter";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { CheckCircle2, AlertTriangle, AlertCircle } from "lucide-react";

const STORAGE_KEY = "admin-banner-collapsed";

interface SystemInfo {
  apiReachable: boolean;
  database: { healthy: boolean; error: string | null };
}

interface RateLimitResponse {
  lockedCount: number;
}

interface PendingReportsResponse {
  pagination: { total: number };
}

interface IngestHealthData {
  summary: { failed: number; needsReview: number };
}

type HealthLevel = "operational" | "attention" | "critical";

function deriveHealthLevel(
  systemInfo: SystemInfo | undefined,
  systemInfoError: boolean,
  lockoutCount: number,
  failedIngests: number,
  pendingReviewCount: number,
): HealthLevel {
  if (systemInfoError) return "critical";
  if (systemInfo && (!systemInfo.database.healthy || !systemInfo.apiReachable)) return "critical";
  if (lockoutCount > 0 || failedIngests > 0 || pendingReviewCount > 0) return "attention";
  return "operational";
}

export function SystemStatusBadge() {
  const { user } = useAuth();
  const [location, setLocation] = useLocation();
  const isAdmin = user?.isAdmin ?? false;
  const isSuperAdmin = user?.isSuperAdmin ?? false;

  const { data: systemInfo, isError: systemInfoError } = useQuery<SystemInfo>({
    queryKey: ["/api/settings/system-info"],
    enabled: isAdmin,
    refetchInterval: 60000,
  });

  const { data: rateLimits, isLoading: lockoutsLoading } = useQuery<RateLimitResponse>({
    queryKey: ["/api/admin/rate-limits"],
    enabled: isAdmin,
    refetchInterval: 60000,
  });

  const { data: pendingReportsData, isLoading: pendingLoading } = useQuery<PendingReportsResponse>({
    queryKey: ["/api/reports/pending?status=pending&limit=50"],
    enabled: isAdmin,
    refetchInterval: 60000,
  });

  const { data: ingestHealth, isLoading: ingestLoading } = useQuery<IngestHealthData>({
    queryKey: ["/api/dashboard/ingest-health"],
    enabled: isSuperAdmin,
    refetchInterval: 60000,
  });

  if (!isAdmin) return null;

  const isLoading = lockoutsLoading || pendingLoading || (isSuperAdmin && ingestLoading);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground hidden sm:inline">System Status</span>
        <Skeleton className="h-6 w-16 sm:w-24" data-testid="skeleton-system-status-badge" />
      </div>
    );
  }

  const lockoutCount = rateLimits?.lockedCount ?? 0;
  const pendingCount = pendingReportsData?.pagination?.total ?? 0;
  const failedIngests = (ingestHealth?.summary.failed ?? 0) + (ingestHealth?.summary.needsReview ?? 0);
  const attentionCount = lockoutCount + pendingCount + failedIngests;

  const healthLevel = deriveHealthLevel(systemInfo, systemInfoError, lockoutCount, failedIngests, pendingCount);

  const handleClick = () => {
    try { localStorage.setItem(STORAGE_KEY, "false"); } catch {}
    window.dispatchEvent(new CustomEvent("admin-banner-visibility", { detail: { expanded: true } }));
    if (location === "/dashboard" || location === "/") {
      window.dispatchEvent(new Event("highlight-admin-callouts"));
    } else {
      setLocation("/dashboard?highlight=admin-callouts");
    }
  };

  if (healthLevel === "operational" && attentionCount === 0) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground hidden sm:inline">System Status</span>
        <Badge
          variant="outline"
          className="text-emerald-600 dark:text-emerald-500 border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-950/30 cursor-pointer hover-elevate"
          onClick={handleClick}
          data-testid="badge-system-status-ok"
        >
          <CheckCircle2 className="h-3.5 w-3.5 sm:mr-1 flex-shrink-0" />
          <span className="whitespace-nowrap hidden sm:inline">OK</span>
        </Badge>
      </div>
    );
  }

  const isCritical = healthLevel === "critical";
  const StatusIcon = isCritical ? AlertCircle : AlertTriangle;
  const badgeColors = isCritical
    ? "text-red-600 dark:text-red-400 border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950/30"
    : "text-amber-600 dark:text-amber-500 border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30";

  return (
    <div className="flex items-center gap-2">
      <span className="text-sm text-muted-foreground hidden sm:inline">System Status</span>
      <Badge
        variant="outline"
        className={`${badgeColors} cursor-pointer hover-elevate`}
        onClick={handleClick}
        data-testid="badge-system-status-attention"
      >
        <StatusIcon className="h-3.5 w-3.5 mr-1 flex-shrink-0" />
        <span className="hidden sm:inline whitespace-nowrap">
          {attentionCount > 0 ? `${attentionCount} ${attentionCount === 1 ? "item" : "items"}` : (isCritical ? "Issue" : "Attention")}
        </span>
        {attentionCount > 0 && <span className="sm:hidden">{attentionCount}</span>}
      </Badge>
    </div>
  );
}
